import FollowUpsAPI from '../../api/followUps';
import { throwErrorMessage } from '../utils/api';
import {
  createAccountContextGuard,
  createAccountRequestGuard,
} from './utils/accountRequestGuard';

const fetchAllRequestGuard = createAccountRequestGuard();
const fetchRequestGuard = createAccountRequestGuard();
const mutationContextGuard = createAccountContextGuard();

// A época tem de ser capturada no disparo e comparada na resposta, como em
// broadcasts.js. Sem isso um ciclo A→B→A engana o guard: ao voltar para a conta
// original o accountId volta a bater e uma resposta anterior à troca é aplicada
// na visão atual. RESET_ACCOUNT_DATA incrementa a época a cada troca, então a
// comparação detecta o reset intermediário.
//
// Helper compartilhado de propósito: fetch e fetchAll montavam o guard inline e
// ficaram sem a checagem, enquanto as mutações usavam beginMutationRequest.
const scopedRequestChecker = (localState, guard, request, rootGetters) => {
  const accountRequestEpoch = localState.accountRequestEpoch;
  return () =>
    localState.accountRequestEpoch === accountRequestEpoch &&
    guard.isCurrent(localState, request, rootGetters);
};

const beginMutationRequest = (
  localState,
  rootGetters,
  { accountId, generation } = {}
) => {
  const request = mutationContextGuard.begin(localState, {
    accountId: accountId ?? rootGetters.getCurrentAccountId,
    generation,
  });
  return {
    isCurrent: scopedRequestChecker(
      localState,
      mutationContextGuard,
      request,
      rootGetters
    ),
  };
};

export const state = {
  accountRequestEpoch: 0,
  records: [],
  allRecords: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingAll: false,
  },
};

export const getters = {
  getFollowUps: $state => conversationId => {
    return $state.records.filter(msg => msg.conversation_id === conversationId);
  },
  getAllFollowUps: $state => $state.allRecords,
  getUIFlags: $state => $state.uiFlags,
  getFollowUp: $state => id => {
    return $state.records.find(msg => msg.id === id);
  },
};

export const actions = {
  fetchAll: async (
    { commit, state: localState = state, rootGetters = {} },
    { accountId, generation } = {}
  ) => {
    const request = fetchAllRequestGuard.begin(localState, {
      accountId: accountId ?? rootGetters.getCurrentAccountId,
      generation,
    });
    const isCurrentRequest = scopedRequestChecker(
      localState,
      fetchAllRequestGuard,
      request,
      rootGetters
    );
    commit('SET_UI_FLAG', { isFetchingAll: true });
    try {
      const response = await FollowUpsAPI.getAll();
      if (isCurrentRequest()) {
        commit('SET_ALL_FOLLOW_UPS', response.data.payload);
      }
      return response.data.payload;
    } catch (error) {
      if (isCurrentRequest()) throwErrorMessage(error);
      return undefined;
    } finally {
      if (isCurrentRequest()) {
        commit('SET_UI_FLAG', { isFetchingAll: false });
      }
    }
  },

  fetch: async (
    { commit, state: localState = state, rootGetters = {} },
    payload
  ) => {
    const normalizedPayload =
      payload && typeof payload === 'object'
        ? payload
        : { conversationId: payload };
    const { conversationId, accountId, generation } = normalizedPayload;
    const request = fetchRequestGuard.begin(localState, {
      accountId: accountId ?? rootGetters.getCurrentAccountId,
      generation,
    });
    const isCurrentRequest = scopedRequestChecker(
      localState,
      fetchRequestGuard,
      request,
      rootGetters
    );
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await FollowUpsAPI.get({ conversationId });
      if (isCurrentRequest()) {
        commit('SET_FOLLOW_UPS', response.data.payload);
      }
      return response.data.payload;
    } catch (error) {
      if (isCurrentRequest()) throwErrorMessage(error);
      return undefined;
    } finally {
      if (isCurrentRequest()) {
        commit('SET_UI_FLAG', { isFetching: false });
      }
    }
  },

  create: async (
    { commit, state: localState = state, rootGetters = {} },
    { conversationId, messageData, accountId, generation }
  ) => {
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
    });
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await FollowUpsAPI.create(conversationId, messageData);
      if (request.isCurrent()) {
        commit('ADD_FOLLOW_UP', response.data);
      }
      return response.data;
    } catch (error) {
      if (request.isCurrent()) throwErrorMessage(error);
      throw error;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isCreating: false });
      }
    }
  },

  update: async (
    { commit, state: localState = state, rootGetters = {} },
    { conversationId, id, messageData, accountId, generation }
  ) => {
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
    });
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await FollowUpsAPI.update(
        conversationId,
        id,
        messageData
      );
      if (request.isCurrent()) {
        commit('UPDATE_FOLLOW_UP', response.data);
      }
      return response.data;
    } catch (error) {
      if (request.isCurrent()) throwErrorMessage(error);
      throw error;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isUpdating: false });
      }
    }
  },

  delete: async (
    { commit, state: localState = state, rootGetters = {} },
    { conversationId, id, accountId, generation }
  ) => {
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
    });
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await FollowUpsAPI.delete(conversationId, id);
      if (request.isCurrent()) {
        commit('DELETE_FOLLOW_UP', id);
      }
    } catch (error) {
      if (request.isCurrent()) throwErrorMessage(error);
      throw error;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isDeleting: false });
      }
    }
  },

  cancel: async (
    { commit, state: localState = state, rootGetters = {} },
    { conversationId, id, accountId, generation }
  ) => {
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
    });
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await FollowUpsAPI.cancel(conversationId, id);
      if (request.isCurrent()) {
        commit('UPDATE_FOLLOW_UP', response.data);
      }
      return response.data;
    } catch (error) {
      if (request.isCurrent()) throwErrorMessage(error);
      throw error;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isUpdating: false });
      }
    }
  },

  retrySend: async (
    { commit, state: localState = state, rootGetters = {} },
    { conversationId, id, accountId, generation }
  ) => {
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
    });
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await FollowUpsAPI.retrySend(conversationId, id);
      if (request.isCurrent()) {
        commit('UPDATE_FOLLOW_UP', response.data);
      }
      return response.data;
    } catch (error) {
      if (request.isCurrent()) throwErrorMessage(error);
      throw error;
    } finally {
      if (request.isCurrent()) {
        commit('SET_UI_FLAG', { isUpdating: false });
      }
    }
  },

  fetchCount: async (context, conversationId) => {
    try {
      const response = await FollowUpsAPI.getCount(conversationId);
      return response.data.count || 0;
    } catch (error) {
      throwErrorMessage(error);
      return 0;
    }
  },
};

export const mutations = {
  SET_ALL_FOLLOW_UPS($state, messages) {
    $state.allRecords = messages;
  },

  RESET_ACCOUNT_DATA($state) {
    $state.accountRequestEpoch = ($state.accountRequestEpoch || 0) + 1;
    $state.records = [];
    $state.allRecords = [];
    $state.uiFlags = {
      isFetching: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isFetchingAll: false,
    };
  },

  SET_FOLLOW_UPS($state, messages) {
    $state.records = messages;
  },

  ADD_FOLLOW_UP($state, message) {
    $state.records.push(message);
    // Also add to allRecords if it exists
    const existsInAll = $state.allRecords.find(msg => msg.id === message.id);
    if (!existsInAll) {
      $state.allRecords.push(message);
    }
  },

  UPDATE_FOLLOW_UP($state, updatedMessage) {
    const index = $state.records.findIndex(msg => msg.id === updatedMessage.id);
    if (index !== -1) {
      $state.records.splice(index, 1, updatedMessage);
    }
    // Also update in allRecords
    const allIndex = $state.allRecords.findIndex(
      msg => msg.id === updatedMessage.id
    );
    if (allIndex !== -1) {
      $state.allRecords.splice(allIndex, 1, updatedMessage);
    }
  },

  DELETE_FOLLOW_UP($state, id) {
    $state.records = $state.records.filter(msg => msg.id !== id);
    $state.allRecords = $state.allRecords.filter(msg => msg.id !== id);
  },

  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
